'use client'

import { type RefObject, useLayoutEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import { UniversalSearch } from '@/components/Refresh/UniversalSearch/UniversalSearch'
import {
    SITE_HEADER_SEARCH_PLACEHOLDER,
    SITE_HEADER_SEARCH_PLACEHOLDER_MOBILE,
} from '@/components/Refresh/UniversalSearch/universalSearchDefaults'
import type { SearchPlacement } from '@/components/Refresh/UniversalSearch/universalSearchTypes'
import { useHeaderSearch } from './HeaderSearchContext'
import classes from './HeaderSearchPortal.module.css'

interface HeaderSearchPortalProps {
    /** Placeholder div in the expanded or compact header that should currently display search. */
    targetRef: RefObject<HTMLDivElement>
    /** Text to show in the search field without submitting it. */
    searchQuery?: string
    placement?: SearchPlacement
}

interface FocusSnapshot {
    element: HTMLInputElement
    selectionStart: number | null
    selectionEnd: number | null
}

/** Saves focus and cursor position because reparenting can blur an input. */
function captureFocus(host: HTMLElement): FocusSnapshot | null {
    const activeElement = document.activeElement
    if (!(activeElement instanceof HTMLInputElement) || !host.contains(activeElement)) {
        return null
    }

    return {
        element: activeElement,
        selectionStart: activeElement.selectionStart,
        selectionEnd: activeElement.selectionEnd,
    }
}

/** Restores the saved input state without scrolling the page. */
function restoreFocus(snapshot: FocusSnapshot | null) {
    if (!snapshot) return

    snapshot.element.focus({ preventScroll: true })
    if (snapshot.selectionStart !== null && snapshot.selectionEnd !== null) {
        snapshot.element.setSelectionRange(snapshot.selectionStart, snapshot.selectionEnd)
    }
}

/** Starts at the previous bounds, then animates into the new layout position. */
function animateMove(host: HTMLElement, previousBounds: DOMRect | null) {
    if (
        !previousBounds ||
        typeof host.animate !== 'function' ||
        window.matchMedia?.('(prefers-reduced-motion: reduce)').matches
    ) {
        return
    }

    const nextBounds = host.getBoundingClientRect()
    if (!nextBounds.width || !nextBounds.height) return

    host.animate(
        [
            {
                transform: `translate(${previousBounds.left - nextBounds.left}px, ${
                    previousBounds.top - nextBounds.top
                }px) scale(${previousBounds.width / nextBounds.width}, ${
                    previousBounds.height / nextBounds.height
                })`,
            },
            { transform: 'none' },
        ],
        { duration: 300, easing: 'ease' },
    )
}

/**
 * On an expanded header, search starts large in that header. After a short
 * scroll it sits in the compact header instead. It is the same field: typed
 * text, the dropdown, and focus stay put, and it actually leaves the expanded
 * header so that content can scroll away.
 *
 * SiteHeader chooses a placeholder div in the expanded or compact header.
 * Search is not a React child of those divs. This component paints one
 * UniversalSearch into a host div, then appendChild moves that host into the
 * chosen placeholder so the field takes up space there. createPortal's
 * container stays the host so UniversalSearch is not remounted.
 */
export function HeaderSearchPortal({ targetRef, searchQuery, placement = 'auto' }: HeaderSearchPortalProps) {
    const { registerOpener } = useHeaderSearch()
    const [host, setHost] = useState<HTMLDivElement | null>(null)

    /**
     * Creates the portal container after hydration and removes it with the
     * header. A layout effect makes it available before the browser paints.
     */
    useLayoutEffect(() => {
        const element = document.createElement('div')
        element.className = classes.host
        setHost(element)

        return () => element.remove()
    }, [])

    /**
     * Reparents the same host instead of changing createPortal's container,
     * which would remount UniversalSearch and lose its local state.
     */
    useLayoutEffect(() => {
        const target = targetRef.current
        if (!host || !target || host.parentElement === target) return

        const previousBounds = host.isConnected ? host.getBoundingClientRect() : null
        const focusSnapshot = captureFocus(host)

        // Cancel an unfinished move so a quick scroll reversal starts cleanly.
        host.getAnimations?.().forEach((animation) => animation.cancel())
        target.appendChild(host)
        restoreFocus(focusSnapshot)
        animateMove(host, previousBounds)
    }, [host, targetRef])

    if (!host) return null

    return createPortal(
        <UniversalSearch
            placement={placement}
            placeholder={SITE_HEADER_SEARCH_PLACEHOLDER}
            mobilePlaceholder={SITE_HEADER_SEARCH_PLACEHOLDER_MOBILE}
            searchQuery={searchQuery}
            analyticsLocation="site-header"
            onRegisterOpener={registerOpener}
        />,
        host,
    )
}
